import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { authService } from '../services/authService';
import {
  SESSION_EXPIRED_EVENT,
  SessionExpiredDetail,
  isJwtExpired,
  emitSessionExpired,
} from '../utils/authEvents';
import {
  AdminPermissionKey,
  getDefaultAdminPermissions,
} from '../constants/adminPermissions';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'SUPER_ADMIN' | 'PUBLISHER' | 'CLIENT' | 'AGENCY';
  clientId?: string;
  adminPermissions?: AdminPermissionKey[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, loginType?: 'admin' | 'publisher' | 'client' | 'agency' | 'portal') => Promise<{ requires2FA?: boolean; tempToken?: string }>;
  verify2FA: (tempToken: string, code: string, loginType?: 'admin' | 'publisher' | 'client' | 'agency' | 'portal') => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isAgency: boolean;
  isPublisher: boolean;
  isClient: boolean;
  hasPermission: (key: AdminPermissionKey | string) => boolean;
  sessionExpiredMessage: string | null;
  clearSessionExpiredMessage: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Hydrate from sessionStorage so a banner survives the hard redirect that
  // performLogout triggers.
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return sessionStorage.getItem('auth:postLogoutMessage');
    } catch {
      return null;
    }
  });

  const clearSessionExpiredMessage = useCallback(() => {
    setSessionExpiredMessage(null);
    try {
      sessionStorage.removeItem('auth:postLogoutMessage');
    } catch {
      // ignore
    }
  }, []);

  // Computes the right login route based on the loginType the user came in with.
  const resolveLoginPath = useCallback((): string => {
    const loginType = localStorage.getItem('loginType');
    if (loginType === 'publisher') return '/publisher';
    if (loginType === 'portal' || loginType === 'client' || loginType === 'agency') return '/login';
    if (loginType === 'admin') return '/login-administrator';

    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (path === '/login' || path.startsWith('/login/')) return '/login';
      if (path === '/agency' || path.startsWith('/agency/')) return '/login';
      if (path === '/client' || path.startsWith('/client/')) return '/login';
      if (path === '/publisher' || path.startsWith('/publisher/')) return '/publisher';
    }

    return '/login-administrator';
  }, []);

  // Single source of truth for clearing local auth state and redirecting.
  // Used by both the explicit logout button and the session-expired flow.
  const performLogout = useCallback((opts?: { reason?: string; redirect?: boolean }) => {
    const redirectPath = resolveLoginPath();

    localStorage.removeItem('token');
    localStorage.removeItem('loginType');
    sessionStorage.removeItem('super_admin_sensitive_pages_auth');

    if (opts?.reason) {
      // Surface the reason on the login page via sessionStorage so the
      // banner survives the hard reload.
      try {
        sessionStorage.setItem('auth:postLogoutMessage', opts.reason);
      } catch {
        // sessionStorage may be unavailable (e.g. private mode) — ignore.
      }
    }

    if (opts?.redirect !== false) {
      // Hard redirect — wipes all in-memory React state, react-query caches,
      // and any context still holding stale data. Critical because otherwise
      // a half-logged-out app keeps firing requests with the bad token.
      window.location.href = redirectPath;
    } else {
      setUser(null);
    }
  }, [resolveLoginPath]);

  // Keep a stable ref so the storage/event listeners always see the latest
  // version even though they're registered once.
  const performLogoutRef = useRef(performLogout);
  useEffect(() => {
    performLogoutRef.current = performLogout;
  }, [performLogout]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        // If the token is already expired locally, don't even hit /me — go
        // straight to the login page with a friendly message.
        if (isJwtExpired(token)) {
          performLogoutRef.current({
            reason: 'Your session has expired. Please log in again.',
            redirect: false,
          });
          setLoading(false);
          return;
        }

        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
        } catch (error) {
          // The axios interceptor has already emitted "session-expired" for
          // a 401, which our listener below handles. For other errors (e.g.
          // network blip), keep the token around so the user can retry.
          if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as any;
            if (axiosError.response?.status === 401) {
              localStorage.removeItem('token');
              setUser(null);
            }
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Listen for session-expired events emitted by the axios interceptor.
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const detail = (event as CustomEvent<SessionExpiredDetail>).detail;
      const message = detail?.message || 'Your session has expired. Please log in again.';

      setSessionExpiredMessage(message);
      // Brief delay so the user sees the snackbar/state update before the
      // hard redirect wipes the page. 700ms is enough for the visual cue
      // without feeling laggy.
      setTimeout(() => {
        performLogoutRef.current({ reason: message, redirect: true });
      }, 700);
    };

    // Cross-tab sync: if another tab clears the token (logout there), this
    // tab should also drop its user state.
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue === null && user) {
        performLogoutRef.current({ redirect: true });
      }
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener('storage', handleStorage);
    };
  }, [user]);

  // Proactively detect an expired session even when no request is in flight.
  // Because queries use refetchOnWindowFocus:false, a tab left open for days
  // would otherwise keep showing stale UI until the user triggers an action.
  // This checks the stored token when the tab regains focus/visibility and on a
  // 60s heartbeat, so an expired session logs the user out promptly with a
  // "session expired" message instead of silently failing operations.
  useEffect(() => {
    const checkTokenFreshness = () => {
      const token = localStorage.getItem('token');
      if (token && isJwtExpired(token)) {
        emitSessionExpired({
          reason: 'TOKEN_EXPIRED',
          message: 'Your session has expired. Please log in again.',
        });
      }
    };

    window.addEventListener('focus', checkTokenFreshness);
    document.addEventListener('visibilitychange', checkTokenFreshness);
    const intervalId = window.setInterval(checkTokenFreshness, 60_000);
    return () => {
      window.removeEventListener('focus', checkTokenFreshness);
      document.removeEventListener('visibilitychange', checkTokenFreshness);
      window.clearInterval(intervalId);
    };
  }, []);

  const login = async (email: string, password: string, loginType: 'admin' | 'publisher' | 'client' | 'agency' | 'portal' = 'admin'): Promise<{ requires2FA?: boolean; tempToken?: string }> => {
    let result;
    if (loginType === 'publisher') {
      result = await authService.loginPublisher(email, password);
    } else if (loginType === 'portal') {
      result = await authService.loginPortal(email, password);
    } else if (loginType === 'client') {
      result = await authService.loginClient(email, password);
    } else if (loginType === 'agency') {
      result = await authService.loginAgency(email, password);
    } else {
      result = await authService.loginAdmin(email, password);
    }
    
    // Check if 2FA is required
    if (result.requires2FA && result.tempToken) {
      return { requires2FA: true, tempToken: result.tempToken };
    }
    
    // Normal login - store token and user
    if (result.token && result.user) {
      localStorage.setItem('token', result.token);
      // Client/agency share /login — persist as portal for logout redirect
      const storedLoginType =
        loginType === 'client' || loginType === 'agency' ? 'portal' : loginType;
      localStorage.setItem('loginType', storedLoginType);
      setUser(result.user);
    }
    
    return {};
  };

  const verify2FA = async (tempToken: string, code: string, loginType: 'admin' | 'publisher' | 'client' | 'agency' | 'portal' = 'admin'): Promise<void> => {
    const { user: userData, token } = await authService.verify2FA(tempToken, code);
    localStorage.setItem('token', token);
    const storedLoginType =
      loginType === 'client' || loginType === 'agency' ? 'portal' : loginType;
    localStorage.setItem('loginType', storedLoginType);
    setUser(userData);
  };

  const logout = (): void => {
    performLogout({ redirect: true });
  };

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAgency = user?.role === 'AGENCY';
  const isPublisher = user?.role === 'PUBLISHER';
  const isClient = user?.role === 'CLIENT';

  const hasPermission = useCallback(
    (key: AdminPermissionKey | string): boolean => {
      if (!user) return false;
      if (user.role === 'SUPER_ADMIN') return true;
      if (user.role !== 'ADMIN') return false;
      const perms = user.adminPermissions ?? getDefaultAdminPermissions();
      return perms.includes(key as AdminPermissionKey);
    },
    [user],
  );

  const value: AuthContextType = {
    user,
    loading,
    login,
    verify2FA,
    logout,
    isAdmin,
    isSuperAdmin,
    isAgency,
    isPublisher,
    isClient,
    hasPermission,
    sessionExpiredMessage,
    clearSessionExpiredMessage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
